/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { css } from "@panda/css";
import { CosmosText } from "../../../atoms/text/CosmosText.atom";
import { useDirectoryContext } from "../core/Directory.context";

// The Directory stack already spaces slots 32px apart; the extra 24px gives the
// 56px gap above the counter (Figma 698:14272) while keeping 32px below it.
const countStyles = css({
	marginTop: "eds.dimension.24",
});

export type DirectoryResultsCountProps = {
	/** Visible copy for the filtered count, e.g. `(n) => \`${n} partners\``. Defaults to "N result(s)". */
	label?: (count: number) => string;
};

/**
 * Visible filtered-result count (e.g. "12 partners"). Only shown while a search
 * or filter is active and at least one item matches — the no-results state
 * covers zero. Not a live region; the Directory root already announces count
 * changes to screen readers.
 */
export function DirectoryResultsCount({ label }: DirectoryResultsCountProps): React.JSX.Element | null {
	const { result } = useDirectoryContext();
	const { searchQueries, singleValueFilters, multiValueFilters } = result.state;
	const count = result.totalFilteredCount;
	const isFiltered =
		Object.values(searchQueries).some((q) => q.trim() !== "") ||
		Object.values(singleValueFilters).some((v) => v !== "") ||
		Object.values(multiValueFilters).some((vs) => vs.length > 0);
	if (!isFiltered || count === 0) return null;

	const text = label ? label(count) : `${count} ${count === 1 ? "result" : "results"}`;
	return (
		<CosmosText variant="uiLG" className={countStyles}>
			{text}
		</CosmosText>
	);
}
